import React, { useEffect, useState } from 'react';
import '../../App.css';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// images
import gearsIcon from '../../icons/gears.gif';
import magnifierWhite from '../../icons/magnifier-white-no-border.512.png';
import magnifierBlack from '../../icons/magnifier-black-no-border.512.png';
import logsIcon from '../../icons/log.svg';

// classes
import { useTranslation } from 'react-i18next';
import Scrollbox from '../../tools/scrollbox';
import PanelButton from './panel-button';
import PleaseWait from '../../tools/please-wait';
import RoundButton from '../../tools/round-button';

// types
import { GatewayLogType, Project, ToolType } from '../../utils/types';

// functions.
import { brw_keyDownHandler } from '../../utils/item-browser';

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

import { store } from '../../app/store';

import { setProjectPanelFilter } from '../../app-slice';
import { GlobalState, rG_setProject } from '../../app/global-slice';
import { setFocusedProjectDiv, setSelectedProjectDiv } from './panels-slice';

//  ------------------------------------------------------------
//
//  Project panel component.
//
//  ------------------------------------------------------------

export default function ProjectPanel(props: {
  sLoadingProjects: boolean;
  sGatewayLogs: GatewayLogType[];
  onClickBtnHandler: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  //
  // translation function
  const { t } = useTranslation();

  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // global state variables.
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );
  const sG_project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_darkMode: boolean = useAppSelector((state) => state.global.darkMode);

  // app state variables.
  const sProjects: Project[] = useAppSelector((state) => state.app.projects);
  const sProjectPanelFilter = useAppSelector((state) => state.app.projectPanelFilter);

  // local state variables.
  const sFocusedProjectDiv: { divID: number } = useAppSelector(
    (state) => state.panels.focusedProjectDiv,
  );
  const sSelectedProjectDiv: { divID: number } = useAppSelector(
    (state) => state.panels.selectedProjectDiv,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  const sFilteredProjects: Project[] = sProjects.filter(
    (project) =>
      project.abbreviation.toUpperCase().includes(sProjectPanelFilter.toUpperCase()) ||
      sProjectPanelFilter === '',
  );

  // check if the selected project is currently hidden by the filter.
  const sSelectedProjectHidden: boolean =
    sFilteredProjects.findIndex((find) => find.id === sG_project?.id) === -1;

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Set the focus to a specific item in the file browser.
  //
  //	------------------------------------------------------------

  const focusDataItem = () => {
    //
    const focusedID: number =
      sFocusedProjectDiv.divID < sFilteredProjects.length
        ? sFilteredProjects[sFocusedProjectDiv.divID].id
        : -1;
    const id: string = sSelectedProjectHidden
      ? 'project_' + focusedID.toString()
      : 'project_' + (sG_project?.id ?? -1).toString();

    window.requestAnimationFrame(() => {
      const target = document.getElementById(id);
      if (target instanceof HTMLButtonElement) {
        target.focus();
      }
    });
    //
  }; // focusDataItem

  //	------------------------------------------------------------
  //
  //	Handler for changes to the input boxes.
  //
  //	------------------------------------------------------------

  const onChangeEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.target;

    if (inputBox.name === 'project-filter') {
      store.dispatch(setProjectPanelFilter(inputBox.value));
    }
    //
  }; // onChangeEvent

  //	------------------------------------------------------------
  //
  //	Handler for when a key is pressed on an item.
  //
  //	------------------------------------------------------------

  function onKeyDownHandler(event: React.KeyboardEvent<HTMLElement>) {
    //
    const stateFetch: any = store.getState();
    const stateGlobal: GlobalState = stateFetch.global;

    // allow the item browser handler to handle this key down event.
    // this handler moves the focus from one item to another..
    brw_keyDownHandler({
      event: event,
      dataItems: sFilteredProjects.length,
      itemsPerRow: 1,
      selectedDivs: [],
      focusElement: sSelectedProjectHidden
        ? sFocusedProjectDiv.divID
        : (sFilteredProjects.findIndex((find) => find.id === stateGlobal.project?.id) ??
          -1),
      setFocusElement: sSelectedProjectHidden
        ? setFocusedProjectDiv
        : setSelectedProjectDiv,
    });
    //
  } // onKeyDownHandler

  //  ------------------------------------------------------------
  //
  //  H O O K S
  //
  //  ------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  Update the selected project when the selected div element
  //  is changed using the keyboard.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    // set the project.
    if (sSelectedProjectDiv.divID > -1) {
      dispatch(rG_setProject(sFilteredProjects[sSelectedProjectDiv.divID]));
    }
    //
  }, [sSelectedProjectDiv]);

  //  ------------------------------------------------------------
  //
  //  Place the focus on the new selected project when the
  //  selected project is changed, or the focused div is changed.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    focusDataItem();
    //
  }, [sG_project, sFocusedProjectDiv]);

  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  return (
    <div
      role="complementary"
      aria-label="Project selection panel"
      className="project-button-container"
      style={{
        flex: '1 1',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: '8px 8px 0px 0px',
        width: '140px',
        margin: '0px 0px 0px 0px',
      }}
      data-pos="B"
    >
      <div
        style={{
          flex: '1 1',
          width: '140px',
          display: sG_loggedIn === true ? 'flex' : 'none',
          flexDirection: 'column',
        }}
      >
        <Scrollbox direction="vertical" flex="1 1" width="135px" flexDirection="column">
          <div
            role="navigation"
            aria-label="Available projects"
            style={{
              flex: '0 0 auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              margin: '0px 6px 0px 6px',
            }}
            onKeyDown={onKeyDownHandler}
          >
            {/* add all the project buttons */}
            <div style={{ flex: '0 0 5px' }} />
            <PleaseWait text="" visible={props.sLoadingProjects === true} />
            {sFilteredProjects.map((project, index) => (
              <PanelButton
                key={index}
                name={'project_' + project.id.toString()}
                icon={project.icon}
                iconBW={project.iconBW}
                onClick={props.onClickBtnHandler}
                selected={
                  sG_project !== undefined && sProjects.length > index
                    ? sG_project.id === project.id
                    : false
                }
                focused={
                  sSelectedProjectHidden === true
                    ? sFocusedProjectDiv.divID === index
                    : false
                }
                projectName={project.abbreviation.toUpperCase()}
                description={
                  project.abbreviation.toUpperCase() + ': ' + project.description
                }
                darkBackground={true}
              />
            ))}
            <div style={{ flex: '0 0 10px' }} />
          </div>
        </Scrollbox>
        <div
          style={{
            flex: '0 0 auto',
            width: '100%',
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: '5px',
          }}
        >
          <div style={{ flex: '0 0 10px' }} />
          <div
            id="lbl-filter-projects"
            className="text-colour"
            data-white="T"
            style={{ margin: '0px 0px 0px 10px', width: '100%' }}
          >
            Filter projects:
          </div>
          <div style={{ flex: '0 0 8px' }} />
          <div className="padding" data-padding="5px" style={{ position: 'relative' }}>
            <img
              src={
                magnifierWhite
              } /*{sG_DarkMode === true ? magnifierWhite : magnifierBlack}*/
              alt="Filter projects"
              width="15px"
              height="15px"
              style={{ position: 'absolute', left: '8px', top: '6px' }}
            />
            <input
              aria-labelledby="lbl-filter-projects"
              className="input-field"
              style={{ paddingLeft: '22px' }}
              type="text"
              id="project-filter"
              name="project-filter"
              placeholder={t('Project')}
              onChange={onChangeEvent}
              value={sProjectPanelFilter}
              maxLength={50}
              data-disabled={props.sLoadingProjects === true ? 'T' : 'F'}
              data-small="T"
              disabled={props.sLoadingProjects === true}
              data-dark-background="T"
            ></input>
          </div>
          <div style={{ flex: '0 0 5px' }} />
        </div>
      </div>

      {/* the flex box that will fill any gap in the column */}
      <div
        style={{
          display: sG_loggedIn !== true ? 'flex' : 'none',
          flex: '1 1',
        }}
      />
    </div>
  );
}
